import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import Logo from "../components/Logo";
import PasswordInput from "../components/PasswordInput";
import api, { APP_URL } from "../services/api";

/**
 * Where the link in the reset email lands.
 *
 * The email carries the address and a one-time token in the query string, so
 * nothing has to be typed here except the new password — somebody who has
 * forgotten their password cannot be asked to prove who they are any other
 * way. The server checks the token and that it has not expired.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  // Kept in step with the server's own rule, so the first thing they hear
  // about a password that will not do is not an error after submitting.
  const tooShort = password.length > 0 && password.length < 8;
  const noLetterOrNumber =
    password.length >= 8 && !(/[a-z]/i.test(password) && /\d/.test(password));
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    !loading && password.length >= 8 && !noLetterOrNumber && confirm === password;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    const res = await api.resetPassword({ email, token, password });
    setLoading(false);
    if (res.ok) setDone(true);
    else setError(res.message || "That reset link is no longer valid.");
  }

  if (!email || !token) {
    return (
      <section className="section-container flex justify-center py-20">
        <div className="card w-full max-w-md p-8 text-center">
          <FiAlertCircle className="mx-auto text-amber-500" size={40} />
          <h1 className="mt-4 text-xl font-bold">This link is incomplete</h1>
          <p className="mt-2 text-sm text-slate-600">
            Open the link from your reset email exactly as it arrived, or ask for a new one.
          </p>
          <Link to="/forgot-password" className="btn-primary mt-6 w-full">
            Send a new reset link
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="section-container flex justify-center py-20">
      <div className="card w-full max-w-md p-8">
        <div className="flex justify-center">
          <Logo withTagline={false} />
        </div>

        {done ? (
          <div className="mt-8 text-center">
            <FiCheckCircle className="mx-auto text-emerald-500" size={40} />
            <h1 className="mt-4 text-xl font-bold">Password changed</h1>
            <p className="mt-2 text-sm text-slate-600">
              You can sign in with your new password now.
            </p>
            <a href={`${APP_URL}/#/login`} className="btn-primary mt-6 block w-full text-center">
              Go to sign in
            </a>
          </div>
        ) : (
          <>
            <h1 className="mt-6 text-center text-2xl font-bold">Choose a new password</h1>
            <p className="mt-1 text-center text-sm text-slate-600">
              Resetting the password for <strong>{email}</strong>.
            </p>

            {error && (
              <div className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {error}{" "}
                <Link to="/forgot-password" className="font-medium underline">
                  Ask for a new link
                </Link>
                .
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6">
              <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="rp-password">
                New Password
              </label>
              <PasswordInput
                id="rp-password"
                required
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {tooShort && (
                <p className="mt-1 text-sm text-red-600">
                  Use at least 8 characters.
                </p>
              )}
              {noLetterOrNumber && (
                <p className="mt-1 text-sm text-red-600">
                  Include at least one letter and one number.
                </p>
              )}

              <label
                className="mb-1.5 mt-4 block text-sm font-medium text-navy"
                htmlFor="rp-confirm"
              >
                Confirm New Password
              </label>
              <PasswordInput
                id="rp-confirm"
                required
                autoComplete="new-password"
                placeholder="Repeat new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {mismatch && (
                <p className="mt-1 text-sm text-red-600">Both passwords must match.</p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Saving..." : "Save New Password"}
              </button>
              {!canSubmit && !loading && (
                <p className="mt-2 text-center text-sm text-slate-500">
                  Fill in both boxes with a matching password of at least 8 characters,
                  including a letter and a number.
                </p>
              )}
            </form>

            <p className="mt-6 text-center text-sm text-slate-600">
              Remembered it?{" "}
              <Link to="/login" className="font-medium text-primary hover:underline">
                Back to Log in
              </Link>
            </p>
          </>
        )}
      </div>
    </section>
  );
}
